import type { Carer } from '@lmc/ts-common'
import { InputSelect, TrainingMatrix } from '@lmc/ui-kit'
import Legend from '@lmc/ui-kit/src/molecules/Legend/Legend'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import useSkillMatrixData from '../hooks/useSkillMatrixData'
import { getColumns, getRows } from '../utils/tableUtils'

export const TABLE_MARGINT_TOP = 11

const TableDetails = styled.div`
  display: flex;
  justify-content: space-between;

  margin-bottom: ${TABLE_MARGINT_TOP}px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    align-items: center;
  }
`

export const InputSelectStyled = styled(InputSelect)`
  width: 40%;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 90%;
  }
`
const LegendStyled = styled(Legend)`
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-top: 10px;

    width: 90%;
    overflow-x: auto;
  }
`

const SkillMatrix = ({ testId }: { testId?: string }) => {
  const [category, setCategory] = useState<string[]>()
  const { data, isLoading, categories } = useSkillMatrixData({
    categories: category
  })
  const { t } = useTranslation()

  const columns = useMemo(() => {
    if (!data) {
      return []
    }

    const newColumns = getColumns(data)

    return newColumns
  }, [data, getColumns])

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const rows = useMemo(() => {
    if (!data || !carers) {
      return []
    }

    const newRows = getRows(data, carers, t)

    return newRows
  }, [data, carers, getRows])

  const onCategoryChange = (value: string[]) => {
    setCategory(value)
  }

  return (
    <div data-testid={testId}>
      <TableDetails
        data-testid={testId ? `${testId}-table-details` : undefined}
      >
        <InputSelectStyled
          testId={testId ? `${testId}-category-filter` : 'category-filter'}
          layout="column"
          type="categories"
          placeholder={t('skillsAndNeeds.table.selectCategoriesToFilter')}
          tooltip={t('skillsAndNeeds.form.categoryTooltip')}
          onChange={onCategoryChange}
          options={categories}
          mode="multiple"
          data-testid={testId ? `${testId}-category-filter` : undefined}
        />
        <LegendStyled data-testid={testId ? `${testId}-legend` : undefined} />
      </TableDetails>
      <TrainingMatrix
        rows={rows}
        columns={columns}
        loading={isLoading}
        data-testid={testId ? `${testId}-matrix` : undefined}
      />
    </div>
  )
}

export default SkillMatrix
